/**
 * 請求書と入金(Gate 収入側・2026-10-23 段階3)。docs/specs/2026-10-全体設計.md 5章。
 * 流れ: 売上台帳(売上の取引)→ 請求書 → 入金予定 → 入金消込。
 *
 *  - 売上は稼働終了などの発生時点で PL に計上済み。**請求書では売上を増やさず**、入金予定(売掛)を作るだけ(二重計上の防止)
 *  - 請求書番号 = 会社略号-YYYYMM-連番(2026-10 決定)。連番は会社×月ごと
 *  - 第1版は**月単位**で請求する(対象月の売上をまとめる)。締め日が末日以外の取引先も月単位(仮決定・全体設計 K9)
 *  - 入金予定日 = 取引先の入金条件(collect_terms)から計算。条件が無ければ「末締め翌月末・後ろ倒し」(仮決定 K10)。
 *    月単位で締めるため「当月払い」の条件で予定日が締め日より前になるときは、翌月の同じ日にする
 *  - 請求書に入れるのは、発行する会社の売上だけ(別の会社の売上を混ぜない)
 *  - 金額は税込。消費税額 = 税込合計 × 10 ÷ 110 の1円未満切り捨て(端数は未決 U5 の仮決定)
 *  - 同じ売上の取引を2枚の請求書に入れない(取消した請求書は除く)
 *  - 状態: 入金0 = 発行済み / 一部 = 一部入金 / 全額以上 = 入金済み(過入金は未決 U3。残高がマイナスで分かる)
 *
 * 具体例は tests/unit/invoice.test.ts
 */
import { endOfMonth } from '@core/lib/date';
import type {
  Account,
  Company,
  DateStr,
  Invoice,
  InvoiceIssuer,
  InvoiceLine,
  InvoiceStatus,
  MonthStr,
  PaymentTerms,
  Transaction,
  Vendor,
  Yen,
} from '@core/lib/types';
import { dueDateFor } from '@core/partners/terms';

export const DEFAULT_COLLECT_TERMS: PaymentTerms = {
  closing_day: 'end',
  month_offset: 1,
  pay_day: 'end',
  holiday_shift: 'after',
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  issued: '未入金',
  partial: '一部入金',
  paid: '入金済み',
  void: '取消',
};

/** 例: SUM-202610-001 */
export function invoiceNumberOf(companyCode: string, month: MonthStr, seq: number): string {
  return `${companyCode}-${month.replace('-', '')}-${String(seq).padStart(3, '0')}`;
}

/** 連番の数え先(counters のID) */
export function invoiceCounterKey(companyCode: string, month: MonthStr): string {
  return `invoice-${companyCode}-${month.replace('-', '')}`;
}

/** 税込の金額に含まれる消費税額(1円未満切り捨て) */
export function taxIncluded(total: Yen, ratePercent = 10): Yen {
  if (total <= 0) return 0;
  return Math.floor((total * ratePercent) / (100 + ratePercent));
}

/** 対象月の請求の締め日と入金予定日 */
export function invoiceDatesFor(month: MonthStr, terms: PaymentTerms | null | undefined): { closingDate: DateStr; dueDate: DateStr } {
  const t = terms ?? DEFAULT_COLLECT_TERMS;
  const closingDate = endOfMonth(month);
  const first = dueDateFor(closingDate, { ...t, closing_day: 'end' });
  let dueDate = first.dueDate;
  // 例: 20日締め当月25日払いの取引先を月単位で締めると、予定日(10/25)が締め日(10/31)より前になる → 翌月(11/25)。
  // 休日の前倒しで前にずれただけ(当月末払い・月末が休日)は、そのままにする
  if (first.scheduledDate < closingDate && t.month_offset < 3) {
    dueDate = dueDateFor(closingDate, { ...t, closing_day: 'end', month_offset: (t.month_offset + 1) as PaymentTerms['month_offset'] }).dueDate;
  }
  return { closingDate, dueDate };
}

/** 請求書に入れられる売上の状態(差戻し・取消は入れない) */
const BILLABLE_STATUSES: Transaction['status'][] = ['applied', 'manager_checked', 'approved', 'cash_ready', 'received', 'paid', 'completed'];

/**
 * 取引先・対象月の「まだ請求書に入っていない売上」。
 * 売上 = 科目の pl_line が revenue の取引(資金移動は除く)
 */
export function unbilledSales(params: {
  transactions: Transaction[];
  accounts: Pick<Account, 'id' | 'pl_line'>[];
  invoices: Pick<Invoice, 'status' | 'lines'>[];
  customerId?: string | null;
  /** 発行する会社。指定したときはその会社の売上だけ */
  companyId?: string;
  month: MonthStr;
}): Transaction[] {
  const { transactions, accounts, invoices, customerId, companyId, month } = params;
  const revenue = new Set(accounts.filter((a) => a.pl_line === 'revenue').map((a) => a.id));
  const billed = new Set(
    invoices.filter((i) => i.status !== 'void').flatMap((i) => i.lines.map((l) => l.transaction_id).filter(Boolean)),
  );
  return transactions
    .filter(
      (t) =>
        t.type !== 'transfer' &&
        revenue.has(t.account_id) &&
        t.posting_month === month &&
        BILLABLE_STATUSES.includes(t.status) &&
        !billed.has(t.id) &&
        (customerId === undefined || (t.vendor_id ?? null) === customerId) &&
        (companyId === undefined || t.company_id === companyId),
    )
    .sort((a, b) => (a.posting_date < b.posting_date ? -1 : a.posting_date > b.posting_date ? 1 : 0));
}

export function lineOf(t: Transaction, describe: (t: Transaction) => string): InvoiceLine {
  return {
    transaction_id: t.id,
    date: t.posting_date,
    description: describe(t),
    department_id: t.department_id,
    account_id: t.account_id,
    amount: t.amount,
  };
}

export type InvoiceDraft = Omit<Invoice, 'id' | 'number' | 'created_by' | 'created_at' | 'updated_by' | 'updated_at'>;

/** 発行する前の請求書(番号は保存するときに採番) */
export function buildInvoiceDraft(params: {
  companyId: string;
  customer: Pick<Vendor, 'id' | 'name' | 'invoice_addressee' | 'postal_code' | 'address' | 'collect_terms'>;
  month: MonthStr;
  issueDate: DateStr;
  lines: InvoiceLine[];
  note?: string | null;
  dueDate?: DateStr | null;
  /** 発行者(会社マスタ)の写し */
  issuer?: InvoiceIssuer | null;
}): InvoiceDraft {
  const { companyId, customer, month, issueDate, lines } = params;
  const total = lines.reduce((s, l) => s + l.amount, 0);
  const { closingDate, dueDate } = invoiceDatesFor(month, customer.collect_terms);
  return {
    company_id: companyId,
    customer_id: customer.id,
    customer_name: customer.name,
    customer_addressee: customer.invoice_addressee ?? null,
    customer_postal_code: customer.postal_code ?? null,
    customer_address: customer.address ?? null,
    issuer: params.issuer ?? null,
    period_month: month,
    issue_date: issueDate,
    closing_date: closingDate,
    due_date: params.dueDate || dueDate,
    lines,
    total,
    tax_rate: 10,
    tax_amount: taxIncluded(total, 10),
    received: 0,
    status: 'issued',
    receipts: [],
    note: params.note?.trim() || null,
    void_reason: null,
  };
}

/** 発行者(会社マスタ)の写し。正式名称が無ければ会社名 */
export function issuerOf(c: Pick<Company, 'name'> & Partial<Pick<Company, 'legal_name' | 'postal_code' | 'address' | 'tel' | 'invoice_reg_no' | 'bank_info'>>): InvoiceIssuer {
  return {
    name: c.legal_name || c.name,
    postal_code: c.postal_code || null,
    address: c.address || null,
    tel: c.tel || null,
    invoice_reg_no: c.invoice_reg_no || null,
    bank_info: c.bank_info || null,
  };
}

/** 発行する前の確認(問題なければ null) */
export function validateDraft(d: Pick<InvoiceDraft, 'issue_date' | 'due_date' | 'lines' | 'total'>, companyCode: string): string | null {
  if (!companyCode.trim()) return '会社の略号が空です(マスタ管理の「会社」で略号を入れてください)';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.issue_date)) return '発行日を入れてください';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.due_date)) return '入金予定日を入れてください';
  if (d.due_date < d.issue_date) return '入金予定日が発行日より前です';
  if (d.lines.length === 0) return '請求書に入れる売上を1件以上選んでください';
  if (d.lines.length > MAX_INVOICE_LINES) return `1枚の請求書に入れられる売上は${MAX_INVOICE_LINES}件までです`;
  if (d.total <= 0) return '請求額が0円以下です';
  return null;
}

/** 1枚の請求書に入れられる売上の数(発行の処理を1回で終えるための上限) */
export const MAX_INVOICE_LINES = 200;

/** 入金の合計から状態を決める(取消は変えない) */
export function statusOf(total: Yen, received: Yen, current?: InvoiceStatus): InvoiceStatus {
  if (current === 'void') return 'void';
  if (received <= 0) return 'issued';
  return received < total ? 'partial' : 'paid';
}

/** 残高(請求額 − 入金済み)。マイナスは過入金 */
export function balanceOf(i: Pick<Invoice, 'total' | 'received' | 'status'>): Yen {
  return i.status === 'void' ? 0 : i.total - i.received;
}

export function validateReceipt(i: Pick<Invoice, 'status'> & Partial<Pick<Invoice, 'received'>>, r: { date: string; amount: number }): string | null {
  if (i.status === 'void') return '取消した請求書には入金を記録できません';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.date)) return '入金日を入れてください';
  if (!Number.isInteger(r.amount) || r.amount === 0) return '金額は0以外の整数で入れてください(返金はマイナス)';
  if (r.amount < 0 && i.received !== undefined && i.received + r.amount < 0) return '返金が入金済みの額を超えています';
  return null;
}

/** 売掛の集計: 未入金の残高・うち入金予定日を過ぎたもの */
export function summarizeReceivables(invoices: Invoice[], today: DateStr): { open: Yen; overdue: Yen; overdueCount: number } {
  let open = 0;
  let overdue = 0;
  let overdueCount = 0;
  for (const i of invoices) {
    const b = balanceOf(i);
    if (b <= 0) continue;
    open += b;
    if (i.due_date < today) {
      overdue += b;
      overdueCount++;
    }
  }
  return { open, overdue, overdueCount };
}
